package com.projectas.app.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.projectas.app.style.TextSelectionStyle
import com.projectas.app.util.BackgroundGradient
import com.projectas.app.util.Constants.ACME_REGULAR
import com.projectas.app.util.Constants.LATO_REGULAR
import com.projectas.app.util.Constants.SUPPORT_EMAIL
import com.projectas.app.util.TextGradient
import com.projectas.app.util.Theme
import com.varabyte.kobweb.compose.css.BackgroundClip
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.backgroundImage
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@OptIn(DelicateApi::class)
@Composable
@Page
fun PrivacyPolicyPage() {
    val breakpoint = rememberBreakpoint()
    val largeBreakpoint = remember(breakpoint) {
        breakpoint > Breakpoint.LG
    }

    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .backgroundImage(BackgroundGradient)
                .padding(
                    leftRight = if (breakpoint > Breakpoint.LG) 120.px
                    else if (breakpoint == Breakpoint.LG) 100.px
                    else if (breakpoint > Breakpoint.MD) 70.px
                    else if (breakpoint == Breakpoint.MD) 50.px
                    else 40.px
                ),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(if (largeBreakpoint) 800.px else 380.px)
                    .padding(bottom = 100.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Main Title
                H1(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 20.px, top = if (largeBreakpoint) 50.px else 20.px)
                        .backgroundImage(TextGradient)
                        .background { this.clip(BackgroundClip.Text) }
                        .color(Color.transparent)
                        .fontFamily(ACME_REGULAR)
                        .fontSize(if (largeBreakpoint) 4.cssRem else 3.cssRem)
                        .lineHeight(1.2)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Privacy Policy")
                }

                // Subtitle Information
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.TextLowVisibility.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.4)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Effective Date: 26.10.2025")
                }

                Surface(modifier = Modifier.height(20.px)) {}

                // Introduction
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text("The Projectas (\"we,\" \"our,\" or \"us\") respects your privacy. This Privacy Policy explains how we collect, use, and protect your information when you use our mobile application (\"App\").")
                }

                // Section 1
                SectionTitle("1. Information We Collect", largeBreakpoint)
                SectionContent(
                    "The Projectas collects only basic account information through third-party authentication providers when you sign in using Google or Apple.",
                )
                SubSectionTitle("This information may include:", largeBreakpoint)
                SectionContent("• Name")
                SectionContent("• Email address")
                SectionContent("• Profile photo (if provided by the authentication provider)")
                SubSectionTitle("We do not collect:", largeBreakpoint)
                SectionContent("• Passwords")
                SectionContent("• Payment information")
                SectionContent("• Location data")
                SectionContent("• Contacts")
                SectionContent("• Messages or communication content")

                // Section 2
                SectionTitle("2. How We Use Your Information", largeBreakpoint)
                SubSectionTitle("We use the collected information only to:", largeBreakpoint)
                SectionContent("- Create and manage your user account")
                SectionContent("- Authenticate and secure access to the app")
                SectionContent("- Display basic profile information within the app")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent(
                    "Your data is not used for advertising, tracking, or profiling.",
                )

                // Section 3
                SectionTitle("3. Data Sharing", largeBreakpoint)
                SectionContent(
                    "We do not sell, rent, or share your personal data with third parties.",
                )
                SubSectionTitle("Authentication is handled securely through:", largeBreakpoint)
                SectionContent("• Google Sign-In")
                SectionContent("• Apple Sign-In")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent(
                    "These providers may process your data according to their own privacy policies.",
                )

                // Section 4
                SectionTitle("4. Data Storage & Security", largeBreakpoint)
                SectionContent(
                    "We take reasonable technical and organizational measures to protect your data from unauthorized access, loss, or misuse.",
                )
                SectionContent(
                    "Only the minimum required information is stored to provide app functionality.",
                )

                // Section 5
                SectionTitle("5. Data Retention", largeBreakpoint)
                SectionContent(
                    "Your personal data is retained only as long as your account exists.",
                )
                SubSectionTitle("If you delete your account:", largeBreakpoint)
                SectionContent(
                    "Your authentication-related data is permanently removed from our systems.",
                )

                // Section 6
                SectionTitle("6. Your Rights", largeBreakpoint)
                SubSectionTitle("You have the right to:", largeBreakpoint)
                SectionContent("- Access your personal data")
                SectionContent("- Request correction of incorrect data")
                SectionContent("- Request deletion of your account and associated data")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent(
                    "You can exercise these rights by contacting us using the details below.",
                )

                // Section 7
                SectionTitle("7. Children's Privacy", largeBreakpoint)
                SectionContent(
                    "The Projectas is not intended for children under the age of 13, and we do not knowingly collect personal data from children.",
                )

                // Section 8
                SectionTitle("8. Changes to This Privacy Policy", largeBreakpoint)
                SectionContent(
                    "We may update this Privacy Policy from time to time.",
                )
                SectionContent("Any changes will be posted within the app or on our official listing.")

                // Section 9
                SectionTitle("9. Contact Us", largeBreakpoint)
                SectionContent(
                    "If you have any questions or concerns about this Privacy Policy, you can contact us at:",
                )
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(topBottom = 10.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text(SUPPORT_EMAIL)
                }
            }
        }
    }
}

@Composable
private fun SectionTitle(
    text: String,
    largeBreakpoint: Boolean,
) {
    Surface(modifier = Modifier.height(30.px)) {}
    H2(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 20.px)
            .color(Theme.Text.rgb)
            .fontFamily(LATO_REGULAR)
            .fontSize(if (largeBreakpoint) 1.8.cssRem else 1.6.cssRem)
            .lineHeight(1.3)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun SubSectionTitle(
    text: String,
    largeBreakpoint: Boolean,
) {
    Surface(modifier = Modifier.height(15.px)) {}
    H3(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 15.px)
            .color(Theme.Text.rgb)
            .fontFamily(LATO_REGULAR)
            .fontSize(if (largeBreakpoint) 1.4.cssRem else 1.3.cssRem)
            .lineHeight(1.3)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun SectionContent(
    text: String,
) {
    P(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 15.px)
            .color(Theme.TextLowVisibility.rgb)
            .fontFamily(LATO_REGULAR)
            .fontSize(1.1.cssRem)
            .lineHeight(1.6)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}